/**
 * The composer.
 *
 * Everything that decides *what* gets sent lives in `lib/compose.ts`; this file
 * is the rectangle. The split is worth keeping: reply-all's address arithmetic
 * and the autosave's identity tracking are the parts with wrong answers that
 * ship silently, and they should not be reachable only by rendering a dialog.
 *
 * # Three things this screen refuses to lie about
 *
 * *Cc and Bcc are hidden until used, but a populated Bcc is never hidden.*
 * Most messages have neither, and two permanently empty rows make the common
 * case worse for the sake of the rare one. But hiding a row that has something
 * in it is how a blind copy the user has forgotten about goes out -- so the
 * toggle only ever collapses an empty row, and the header says so when there is
 * a blind copy on the message.
 *
 * *A send that returned 200 is queued, not sent.* The daemon writes an outbox
 * row and drains it later. This window used to stay open saying "queued" for
 * half a minute because there was nowhere else for that fact to live; there is
 * now -- `ui/Outbox.tsx` -- so it closes as soon as the message is on the strip
 * and not one instant before. What changed is where the truth is told, not
 * whether it is.
 *
 * *An account with no submission host says so before the first keystroke.*
 * Discovering it after eight hundred words is the failure the probe exists to
 * prevent, and the message names the command that fixes it.
 */

import { useEffect, useRef, useState } from "preact/hooks";

import * as compose from "../lib/compose";
import * as composeWindow from "../lib/composeWindow";
import { fileSize, plural } from "../lib/format";
import * as store from "../lib/store";
import type { Envelope } from "../lib/types";
import { AccountDot } from "./AccountColour";
import { RichEditor } from "./Editor";
import * as Icon from "./Icons";
import { RecipientField, dragging as recipientDrag } from "./RecipientField";

/**
 * Re-exported so the reader can open a composer without reaching into
 * `lib/compose.ts` -- and because `openReply` was already imported from here,
 * and quietly moving it would break the button that uses it.
 *
 * These now go through `lib/composeWindow.ts` rather than straight to
 * `lib/compose.ts`, and that is the whole of what feature "compose opens in its
 * own window" changed at the call sites: on the desktop each raises a window,
 * in a browser each raises the layer below. Nothing here asks which build it is
 * in -- see that module for where the question is asked and why it is asked
 * exactly once.
 */
export const openNew = composeWindow.writeNew;
export const openForward = composeWindow.forward;

/** Reply to the sender. */
export function openReply(row: Envelope): void {
  void composeWindow.reply(row, false);
}

/** Reply to the sender and everyone else on the message, minus the user. */
export function openReplyAll(row: Envelope): void {
  void composeWindow.reply(row, true);
}

export function Compose() {
  const d = compose.draft.value;
  const accounts = store.accounts.value;
  const phase = compose.sendPhase.value;
  // The one round trip that offers the message to the daemon, and nothing
  // longer. Every field is frozen for it, because a subject edited between the
  // request and the response would not be in the message that went.
  const locked = phase === "sending";

  const [confirming, setConfirming] = useState<compose.Concern[] | null>(null);
  const shell = useRef<HTMLDivElement | null>(null);
  const file = useRef<HTMLInputElement | null>(null);

  // A populated Bcc is always on screen, whatever the toggle says. So are
  // both rows while a recipient is being dragged, so there is somewhere to
  // drop it.
  const moving = recipientDrag.value !== null;
  const showBcc = d.showBcc || d.bcc.length > 0 || moving;
  const showCc = d.showCc || d.cc.length > 0 || moving;
  // Pictures the body shows by `cid` are part of the body, not of this list.
  const listed = d.attachments.filter((a) => !a.cid);

  /*
   * The quoted original's pictures, fetched once per set of them and handed to
   * the editor as `data:` URIs. The key is the ids themselves: the editor
   * reloads when the set changes (a reply opened over another), and not
   * otherwise.
   */
  const cidsKey = d.attachments
    .filter((a) => a.cid)
    .map((a) => a.cid)
    .join(",");
  const [cids, setCids] = useState<{ key: string; map: Record<string, string> }>({
    key: "",
    map: {},
  });
  useEffect(() => {
    if (d.html === null || cidsKey === "") return;
    let live = true;
    void compose.inlineImages(d).then((map) => {
      if (live) setCids({ key: cidsKey, map });
    });
    return () => {
      live = false;
    };
    // The draft object changes on every keystroke; the ids are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cidsKey, d.html === null]);
  // The editor reads its pictures once, at load, so it waits for them.
  const editorReady = cidsKey === "" || cids.key === cidsKey;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;

      // A recipient field with its dropdown open owns Escape: the first press
      // dismisses the list, the second closes the composer. Asked by inspecting
      // the target rather than by letting the field call `stopPropagation`,
      // because this listener is on the capture phase at `window` and therefore
      // runs *before* the field's own handler -- the composer would already be
      // closing by the time the field objected.
      if ((e.target as HTMLElement | null)?.getAttribute("aria-expanded") === "true") return;

      e.stopPropagation();
      if (!locked) void compose.closeAfterSaving();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [locked]);

  // Save when the tab goes away. The autosave timer does not fire in a
  // backgrounded tab reliably, and this is the moment someone walks off.
  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === "hidden") void compose.saveNow();
    };
    document.addEventListener("visibilitychange", onHidden);
    return () => document.removeEventListener("visibilitychange", onHidden);
  }, []);

  function attach(files: FileList | null): void {
    if (!files || files.length === 0) return;
    if (!compose.canAttachNewFiles.value) {
      // Named as a limit of the daemon rather than reported as a generic
      // failure. `/jmap/upload` is routed but not implemented, so the session
      // advertises `maxSizeUpload: 0` -- nothing about this file is wrong, and
      // a user told "upload failed" will go and try a smaller one.
      store.toast(
        "This daemon cannot accept new files yet. Forwarding an attachment that is already on " +
          "the server still works.",
        "error",
      );
      return;
    }
    void (async () => {
      for (const f of Array.from(files)) {
        try {
          const added = await compose.uploadBlob(d.accountId, f);
          compose.patch({ attachments: [...compose.draft.value.attachments, added] });
        } catch (e) {
          store.toast(
            `${f.name} was not attached: ${e instanceof Error ? e.message : String(e)}`,
            "error",
          );
        }
      }
    })();
  }

  function trySend(): void {
    const found = compose.concerns();
    if (found.length > 0) {
      setConfirming(found);
      return;
    }
    setConfirming(null);
    void compose.send();
  }

  const submittable = compose.canSendFrom.value;

  return (
    <div class="compose" role="dialog" aria-label="Compose message" ref={shell}>
      <div class="compose-head">
        <Icon.Pencil />
        <span class="grow">{d.subject.trim() || "New message"}</span>
        {d.bcc.length > 0 && (
          <span class="bcc-badge" title="This message has blind copies">
            {plural(d.bcc.length, "blind copy", "blind copies")}
          </span>
        )}
        <button
          class="icon-btn"
          aria-label="Close composer"
          title="Close — the draft is saved to the server first"
          disabled={locked}
          onClick={() => void compose.closeAfterSaving()}
        >
          <Icon.Close />
        </button>
      </div>

      {submittable === "no" && (
        <p class="compose-banner" role="status">
          <Icon.Warning />
          <span>{compose.submissionProblem.value}</span>
        </p>
      )}

      {/*
        `deep` makes the gaps and labels of this area drag the window -- in a
        compose window there is no other titlebar left to grab. Everything
        interactive in here is exempted by Tauri's drag script (inputs,
        selects, buttons) or shields itself (`.rf-box`), and outside the
        desktop the attribute is inert: no drag script is injected.
      */}
      <div class="compose-fields" data-tauri-drag-region="deep">
        {accounts.length > 1 && (
          <div class="compose-line">
            <span class="rf-label">From</span>
            {/*
              The colour of whichever account is currently selected, beside the
              control that selects it.

              Sending from the wrong account is the mistake on this screen that
              cannot be taken back, and it happens because the From line is a
              row of text that looks the same whatever it says. A colour that
              changes as the selection changes is noticed without being read.
              It cannot be inside the `<option>`s -- browsers do not reliably
              style those -- and it does not need to be: the select's own value
              is what a screen reader announces, so this is decoration on top of
              an answer that is already there, and it is `aria-hidden`.
            */}
            <span class="rf-from-colour">
              <AccountDot accountId={d.accountId} />
            </span>
            <select
              class="rf-from"
              value={d.accountId}
              disabled={locked}
              onChange={(e) => {
                const accountId = (e.target as HTMLSelectElement).value;
                compose.patch({ accountId });
                void compose.probeSubmission(accountId);
              }}
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <RecipientField
          label="To"
          values={d.to}
          autofocus={d.to.length === 0}
          onChange={(to) => compose.patch({ to })}
          // On the To row rather than on a row of their own: the row they
          // would have occupied is body space, and these are used once per
          // message at most. Each disappears once its row is open.
          trailing={
            !showCc || !showBcc ? (
              <>
                {!showCc && (
                  <button
                    class="linkish"
                    aria-label="Add Cc recipients"
                    onClick={() => compose.patch({ showCc: true })}
                  >
                    Cc
                  </button>
                )}
                {!showBcc && (
                  <button
                    class="linkish"
                    aria-label="Add blind-copy recipients"
                    onClick={() => compose.patch({ showBcc: true })}
                  >
                    Bcc
                  </button>
                )}
              </>
            ) : null
          }
        />

        {showCc && (
          <RecipientField
            label="Cc"
            values={d.cc}
            onChange={(cc) => compose.patch({ cc })}
          />
        )}

        {showBcc && (
          <RecipientField
            label="Bcc"
            values={d.bcc}
            onChange={(bcc) => compose.patch({ bcc })}
            hint={
              d.bcc.length > 0
                ? "No recipient sees this row, including the people in it."
                : undefined
            }
          />
        )}

        <div class="compose-line">
          <span class="rf-label">Subject</span>
          <input
            type="text"
            class="rf-subject"
            value={d.subject}
            disabled={locked}
            onInput={(e) => compose.patch({ subject: (e.target as HTMLInputElement).value })}
          />
        </div>
      </div>

      {listed.length > 0 && (
        <div class="attach-summary">
          {plural(listed.length, "attachment")} ·{" "}
          {fileSize(listed.reduce((sum, a) => sum + a.size, 0))}
        </div>
      )}
      {listed.length > 0 && (
        <ul class="attach-strip" aria-label="Attachments">
          {listed.map((a) => (
            <li key={a.blobId} class="attach-pill">
              <Icon.Paperclip />
              <span class="attach-name" title={a.name}>
                {a.name}
              </span>
              <span class="attach-size">{fileSize(a.size)}</span>
              <button
                class="chip-x"
                aria-label={`Remove ${a.name}`}
                disabled={locked}
                onClick={() =>
                  compose.patch({
                    attachments: d.attachments.filter((x) => x.blobId !== a.blobId),
                  })
                }
              >
                <Icon.Close />
              </button>
            </li>
          ))}
        </ul>
      )}

      {d.html !== null && !editorReady ? (
        <div class="editor" aria-busy="true" />
      ) : d.html !== null ? (
        /*
          Keyed on the composer's generation, not on the draft: the editor
          reads the markup once and edits flow out of it, so neither a
          re-render nor the first save (which gives the draft an id) may
          reload the frame. A new message, a switch of format, or a different
          set of pictures each give it a fresh start.
        */
        <RichEditor
          key={`${compose.generation.value}:${cidsKey}`}
          initialHtml={d.html}
          cids={cids.map}
          disabled={locked}
          onChange={compose.fromEditor}
          onBlur={() => void compose.saveNow()}
          onEscape={() => {
            if (!locked) void compose.closeAfterSaving();
          }}
          onPlainText={() => compose.setFormat(false)}
        />
      ) : (
        <>
          <textarea
            value={d.body}
            disabled={locked}
            aria-label="Message"
            placeholder="Write something…"
            onInput={(e) => compose.patch({ body: (e.target as HTMLTextAreaElement).value })}
            onBlur={() => void compose.saveNow()}
          />
          <button
            type="button"
            class="btn ghost small compose-format"
            disabled={locked}
            title="Switch this message to rich text"
            onClick={() => compose.setFormat(true)}
          >
            Rich text
          </button>
        </>
      )}

      {compose.saveError.value && (
        <p class="compose-banner error" role="alert">
          <Icon.Warning />
          <span>{compose.saveError.value}</span>
        </p>
      )}

      {confirming && (
        <div class="compose-confirm" role="alertdialog" aria-label="Before sending">
          <span class="grow">
            {confirming.map((c) => c.text).join(" ")} Send it anyway?
          </span>
          <button
            class="btn primary"
            onClick={() => {
              setConfirming(null);
              void compose.send();
            }}
          >
            Send anyway
          </button>
          <button class="btn" onClick={() => setConfirming(null)}>
            Keep editing
          </button>
        </div>
      )}

      {phase !== "idle" && (
        <div class={`compose-status ${phase}`} role="status" aria-live="polite">
          {phase === "sending" && <span class="spinner" />}
          <span class="grow">{compose.sendNote.value}</span>
          {phase === "failed" && (
            <button class="btn" onClick={compose.backToEditing}>
              Back to the message
            </button>
          )}
        </div>
      )}

      {/* A second grab surface, same rules as the fields above. */}
      <div class="compose-foot" data-tauri-drag-region="deep">
        <button
          class="btn primary"
          disabled={!compose.canSend() || confirming !== null}
          title={
            submittable === "no"
              ? "This account has no configured way to send mail"
              : "Send"
          }
          onClick={trySend}
        >
          <Icon.Send />
          Send
        </button>

        <button
          class="btn"
          disabled={locked}
          title={
            compose.canAttachNewFiles.value
              ? "Attach a file"
              : "This daemon cannot accept new files yet — forwarded attachments still work"
          }
          onClick={() => file.current?.click()}
        >
          <Icon.Paperclip />
          Attach
        </button>
        <input
          ref={file}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            attach((e.target as HTMLInputElement).files);
            (e.target as HTMLInputElement).value = "";
          }}
        />

        <span class="grow compose-save">{saveLabel()}</span>

        <button
          class="btn danger ghost"
          disabled={locked}
          onClick={() => void compose.discard()}
        >
          Discard
        </button>
      </div>

    </div>
  );
}

/**
 * What the footer says about the draft.
 *
 * Specific about where it is. "Saved" on its own would let someone close the
 * tab believing a copy exists somewhere it does not; the whole point of storing
 * drafts upstream is that the answer is "in Drafts, on the server", and that is
 * what it should read.
 */
function saveLabel(): string {
  switch (compose.saveState.value) {
    case "saving":
      return "Saving to Drafts…";
    case "saved":
      return "Saved to Drafts on the server.";
    case "dirty":
      return "Unsaved changes — saved a few seconds after you stop typing.";
    case "failed":
      return "Not saved. Your text is still here.";
    case "clean":
      return compose.draftId.value ? "Saved to Drafts on the server." : "";
  }
}
